import { useEffect, useState } from 'react';
import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { useGetAdminUserOverviewQuery } from '@/apis/extended/admin/adminWorkspaceApi';
import { LoanApplicationShell } from '@/components/CommercialLoanApplication/LoanApplicationShell';

/**
 * "View as user": the borrower's loan application screens exactly as they
 * see them, with every field and action read-only. Nothing on this page can
 * save, sign or submit.
 */
const AdminUserViewAsPage = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useGetAdminUserOverviewQuery(userId, {
    skip: !userId,
  });
  const [fileId, setFileId] = useState<string>('');

  useEffect(() => {
    if (!fileId && data?.loanFiles.length) setFileId(data.loanFiles[0]._id);
  }, [data, fileId]);

  if (isLoading || !data) return <LinearProgress />;

  return (
    <Box sx={{ pb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/admin/user/${userId}`)}
        sx={{ mb: 1 }}
      >
        Back to {data.user.displayName}
      </Button>
      <Alert severity="info" sx={{ mb: 2 }}>
        You're seeing {data.user.displayName}'s application as they see it.
        Read-only: nothing here can be changed, signed or submitted.
      </Alert>
      {data.loanFiles.length === 0 ? (
        <Typography color="text.secondary">
          {data.user.displayName} hasn't started a loan application.
        </Typography>
      ) : (
        <>
          {data.loanFiles.length > 1 && (
            <Tabs
              value={fileId}
              onChange={(_e, v) => setFileId(v)}
              variant="scrollable"
              sx={{ mb: 2 }}
            >
              {data.loanFiles.map((f) => (
                <Tab
                  key={f._id}
                  value={f._id}
                  label={`${
                    f.flowProfile === 'refinance' ? 'Refinance' : 'Purchase'
                  }${f.address ? ` · ${f.address}` : ''}`}
                />
              ))}
            </Tabs>
          )}
          {fileId && (
            <LoanApplicationShell key={fileId} applicationId={fileId} viewAs />
          )}
        </>
      )}
    </Box>
  );
};

export { AdminUserViewAsPage };
